<template>
    <div class="plan-modal default-bkg p-2 pb-5 lh-reset">
        <h3 class="mt-2 mb-2">{{ $l10n.__('Paramétrage') }}</h3>

        <div class="flex-level well">
            <p class="m-0">{{ $l10n.__('Accéder aux paramètres de votre plan de relance') }}</p>
            <CompassButton
                :label="$t.__('preferences.Dunning.Plan.setting_access')"
                type="secondary"
                :loading="loading"
                class="flex-item-right"
                @click="planSettingVisible = true"
            />
        </div>

        <CompassDialog
            :visible.sync="planSettingVisible"
            size="large"
            :title="$t.__('preferences.Dunning.Plan.setting')"
        >
            <template #default>
                <div class="dialog__default">
                    <p class="m-0">{{ $l10n.__('Nom de ma relance') }}</p>
                    <el-input v-model="currentName" class="w-4"></el-input>

                    <h4 class="mb-1 mt-5">
                        {{ $l10n.__("Jours d'émission des envois de relance") }}
                    </h4>

                    <span class="grey mb-3 block">
                        {{
                            $l10n.__(
                                "Attention ce paramètre peut créer des décalages dans l'envoi des emails.",
                            )
                        }}
                    </span>

                    <el-checkbox-group v-model="formWeekdays">
                        <el-checkbox
                            v-for="weekday in weekDays"
                            :key="weekday.binary"
                            :label="weekday.binary"
                        >
                            {{ weekday.weekDay }}
                        </el-checkbox>
                    </el-checkbox-group>

                    <h4 class="mb-1 mt-5">{{ $l10n.__('Choix de la plage horaire') }}</h4>

                    <div class="flex-level">
                        <span class="mr-3">{{ $l10n.__('Envoyer les emails entre') }}</span>

                        <el-time-select
                            v-model="startTime"
                            placeholder="Start time"
                            class="w-100"
                            :pickerOptions="{
                                start: '00:00',
                                step: '01:00',
                                end: '23:00',
                                maxTime: endTime,
                            }"
                        >
                        </el-time-select>

                        <span class="mx-3">{{ $l10n.__('et') }}</span>

                        <el-time-select
                            v-model="endTime"
                            placeholder="End time"
                            class="w-100"
                            :pickerOptions="{
                                start: '00:00',
                                step: '01:00',
                                end: '23:00',
                                minTime: startTime,
                            }"
                        >
                        </el-time-select>
                    </div>

                    <dunning-enable
                        v-if="doctype == 'invoice'"
                        :enabled.sync="currentRecurring"
                        :enableLabel="$t.__('preferences.Dunning.activated')"
                        :disableLabel="$t.__('preferences.Dunning.disabled')"
                        :description="
                            $t.__('preferences.Dunning.Plan.activate_dunning_invoice_subscription')
                        "
                        class="my-5"
                    />

                    <div class="well fs-3 p-3 my-5">
                        <CompassNumberInput
                            :modelValue="currentGap"
                            :min="1"
                            :max="15"
                            :step="1"
                            class="fright mt-2 p-0 custom-numbertinput"
                            @change="(newGap) => (currentGap = newGap)"
                        />

                        <div class="m-0">
                            <span class="semibold mt-2">
                                {{ $l10n.__('Nombre de jours minimum entre deux relances') }} </span
                            ><br />
                            <p class="grey mt-1 mb-0 wp-6">
                                {{
                                    $l10n.__(
                                        "Ce paramètre permet d'éviter de multiples relances sur la période indiquée si le plan de relance est par exemple modifié.",
                                    )
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="well dialog__default--delete">
                        <p class="dialog__default--message m-0 mt-1 semibold wordbreak">
                            {{ deleteMessage }}
                        </p>
                        <CompassButton
                            :label="$t.__('preferences.Dunning.Plan.delete_dunning_plan')"
                            type="primary-destructive"
                            :disabled="!plan.deletable"
                            class="ml-2 fright"
                            @click="confirmationVisible = true"
                        />
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        @click="planSettingVisible = false"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :loading="loading"
                        class="ml-3"
                        @click="update"
                    />
                </div>
            </template>
        </CompassDialog>

        <step :planId="plan.id" :steps="plan.steps" />

        <CompassDialog
            :visible.sync="confirmationVisible"
            size="small"
            :title="$t.__('preferences.Dunning.Plan.deleting_plan')"
        >
            <template #header>
                <div class="dialog__header">
                    +
                    <div class="dialog__header--close">
                        <CompassIconButton icon="close" @click="$emit('close')" />
                    </div>
                </div>
            </template>
            <template #default>
                <div class="dialog__default">
                    <span class="typographies-paragraph-medium-regular">
                        {{
                            $l10n.__(
                                'Confirmez-vous vouloir supprimer le plan de relance "%(planName)s"',
                                {
                                    planName: plan.name,
                                },
                            )
                        }}
                    </span>
                </div>
            </template>
            <template #footer>
                <div class="dialog__footer">
                    <CompassButton
                        :label="$t.__('common.actions.cancel')"
                        type="secondary"
                        size="medium"
                        @click="confirmationVisible = false"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.delete')"
                        type="primary-destructive"
                        size="medium"
                        :loading="loading"
                        class="ml-3"
                        @click="confirmDelete(plan.id)"
                    />
                </div>
            </template>
        </CompassDialog>
    </div>
</template>
<script>
import DunningEnable from '../DunningEnable.vue';
import Step from './Step.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapState, mapActions } = createNamespacedHelpers('dunning');

export default {
    components: {
        DunningEnable,
        Step,
    },
    props: {
        plan: {
            required: true,
            type: Object,
        },
    },
    data() {
        return {
            currentName: '',
            formWeekdays: [],
            startTime: '08:00',
            endTime: '18:00',
            currentRecurring: false,
            currentGap: 2,
            enabled: false,

            confirmationVisible: false,
            planSettingVisible: false,
        };
    },
    computed: {
        currentHours() {
            const startHour =
                this.startTime == null ? 8 : parseInt(this.startTime.split(':')[0], 10);
            const endHour = this.startTime == null ? 18 : parseInt(this.endTime.split(':')[0], 10);
            const hours = [];

            for (let index = 0; index <= endHour - startHour; index++) {
                const binary = this.hours.find((hour) => hour.hour == startHour + index);
                hours.push(parseInt(binary.binary));
            }
            return hours;
        },
        currentWeekdays() {
            return this.formWeekdays.sort((a, b) => {
                if (a > b) {
                    return 1;
                } else if (a < b) {
                    return -1;
                }
                return 0;
            });
        },
        deleteMessage() {
            if (this.plan.deletable) {
                return this.$l10n.__('Vous pouvez supprimer ce plan');
            } else {
                return this.$l10n.__(
                    'Vous ne pouvez pas supprimer ce plan de relance car il est affecté à des clients ou ce plan est celui par défaut',
                );
            }
        },
        /**
         * When need to display save button
         */
        saveButton() {
            if (this.currentName != this.plan.name) {
                return true;
            }
            if (this.isDiffArray(this.currentWeekdays, this.plan.weekdays)) {
                return true;
            }
            if (this.isDiffArray(this.currentHours, this.plan.hours)) {
                return true;
            }
            if (this.currentRecurring != this.plan.recurring) {
                return true;
            }
            if (this.currentGap != this.plan.gap) {
                return true;
            }

            return false;
        },
        ...mapState(['doctype', 'hours', 'weekDays', 'loading']),
    },
    created() {
        this.currentName = this.plan.name;
        this.formWeekdays = this.plan.weekdays;
        this.startTime = this.binaryToHour(this.plan.hours[0]);
        this.endTime = this.binaryToHour(this.plan.hours[this.plan.hours.length - 1]);
        this.currentRecurring = this.plan.recurring;
        this.currentGap = this.plan.gap;
        this.enabled = this.plan.enabled;
    },
    methods: {
        binaryToHour(binary) {
            const hour = this.hours.find((hour) => hour.binary == binary);

            if (hour.hour < 10) {
                return '0' + hour.hour + ':00';
            }
            return hour.hour + ':00';
        },
        isDiffArray(array1, array2) {
            if (array1.length !== array2.length) {
                return true;
            }

            for (let index = 0; index < array1.length; index++) {
                if (array1[index] !== array2[index]) {
                    return true;
                }
            }

            return false;
        },
        update() {
            const plan = {};
            Object.assign(plan, this.plan);
            plan.name = this.currentName;
            plan.weekdays = this.currentWeekdays;
            plan.hours = this.currentHours;
            plan.recurring = this.currentRecurring;
            plan.gap = this.currentGap;
            delete plan.deletable;
            delete plan.steps;
            this.updatePlan(plan)
                .then(() => {
                    Push.success(this.$l10n.__('Mise à jour réussie'));
                    this.planSettingVisible = false;
                })
                .catch((error) => {
                    const message = error.response.data.error.message;
                    Push.error(message[Object.keys(message)[0]]);
                });
        },
        toggleEnable() {
            const plan = this.plan;
            plan.enabled = !plan.enabled;
            delete plan.deletable;
            this.updatePlan(plan)
                .then(() => {
                    Push.success(this.$l10n.__('Mise à jour réussie'));
                })
                .catch((error) => {
                    const message = error.response.data.error.message;
                    Push.error(message[Object.keys(message)[0]]);
                });
        },
        confirmDelete(planId) {
            this.confirmationVisible = false;
            this.deletePlan(planId).then(() => {
                Push.info(this.$l10n.__('Plan correctement supprimé'));
            });
            this.planSettingVisible = false;
        },
        ...mapActions(['updatePlan', 'deletePlan']),
    },
};
</script>

<style lang="scss" scoped>
.plan-modal {
    .dialog {
        &__default {
            padding-bottom: var(--spacing-4);
            &--delete {
                display: flex;
                flex-direction: row-reverse;
                justify-content: space-between;
                align-items: center;

                .compass-button {
                    white-space: nowrap;
                }
            }
            &--message {
                font-size: 14px;
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 var(--spacing-5) var(--spacing-4);
        }
    }
}
.well.dialog__default--delete {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    align-items: center;
}
</style>
